import React, { FunctionComponent, useMemo, useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import HighchartsReact from 'highcharts-react-official';
import * as Highcharts from 'highcharts';
import { escape, find, map } from 'lodash';

// Tornado shares the Waterfall result payload and formatting helpers
import { GRADIENT_CONFIG } from '../Waterfall/helpers/constants';
import { getFormatConfig, formatValue } from '../Waterfall/helpers/formatUtils';
import { generateTooltipHTML } from '../Waterfall/helpers/tooltipUtils';

/** Highcharts font sizes, overridable per host: the Reports page keeps the historical defaults; the
 * Executive View passes a Duplo-scaled set (Highcharts styles are px strings and don't inherit MUI
 * typography, so the scale has to be threaded explicitly). */
export interface TornadoChartSizes {
  title: number;
  barLabel: number;
  axisTitle: number;
  axisValue: number;
  dataLabel: number;
}

const DEFAULT_SIZES: TornadoChartSizes = {
  title: 18,
  barLabel: 14,
  axisTitle: 14,
  axisValue: 13,
  dataLabel: 12,
};

export interface TornadoChartCoreProps {
  data: any[];
  formatKey: number | null | undefined;
  outputName: string;
  columns?: Array<{ name: string; inputs: Array<{ Name: string }> }>;
  sizes?: Partial<TornadoChartSizes>;
}

/**
 * Host-agnostic horizontal-bar renderer for Tornado results — the chart, chip bar-labels, and the
 * per-bar inputs popover, with no layout chrome. The Reports page wraps this in its Slide/Container
 * (TornadoChartPanel); the Executive View answer view hosts it directly.
 *
 * The result rows arrive in the same shape as Waterfall's: [Scenario A summary, ...column effects,
 * Scenario B summary]. Each effect was calculated independently against Scenario A, so only the
 * effect rows are drawn, sorted by absolute magnitude (largest on top).
 */
const TornadoChartCore: FunctionComponent<TornadoChartCoreProps> = ({
  data,
  formatKey,
  outputName,
  columns,
  sizes: sizeOverrides,
}) => {
  const sizes = { ...DEFAULT_SIZES, ...sizeOverrides };

  // Bar-label popover: clicking a bar's chip label lists the forecast inputs that bar aggregates,
  // straight from the report configuration (no extra fetch). Anchored to the click coordinates, not the
  // label element — opening the popover re-renders the chart, which destroys the clicked label node.
  const [barPopover, setBarPopover] = useState<{
    left: number;
    top: number;
    barName: string;
  } | null>(null);
  const [inputFilter, setInputFilter] = useState('');

  const barInputNames = useMemo(() => {
    if (!barPopover) return [];
    const column = find(columns, { name: barPopover.barName });
    return map(column?.inputs ?? [], 'Name');
  }, [columns, barPopover]);

  const filteredInputNames = inputFilter
    ? barInputNames.filter((n) =>
        n.toLowerCase().includes(inputFilter.toLowerCase())
      )
    : barInputNames;

  const handleLabelClick = (e: React.MouseEvent<HTMLElement>) => {
    const chip = (e.target as HTMLElement).closest('.tornado-bar-label');
    if (!chip || !columns?.length) return;
    const rect = chip.getBoundingClientRect();
    setInputFilter('');
    setBarPopover({
      left: rect.right + 8,
      top: rect.top + rect.height / 2,
      barName: chip.textContent ?? '',
    });
  };

  const effectRows =
    data && data.length > 2 ? data.slice(1, data.length - 1) : data ?? [];

  // Baseline (A3): the effect bars are deltas that deviate from the zero
  // reference line, and that zero line IS Scenario A's baseline — every effect
  // was calculated against it (see the row-shape note above). data[0] is the
  // Scenario A summary, so its Value is the baseline the tornado is measured
  // from. Shown as the label on the existing zero plotLine below, so the reader
  // sees what the bars deviate from. Absent when the payload has no summary row.
  const baselineValue = data && data.length > 2 ? data[0]?.Value : undefined;

  const sortedRows = [...effectRows].sort(
    (a, b) => Math.abs(b.Value) - Math.abs(a.Value)
  );

  const config = getFormatConfig(formatKey);
  // Axis and bar labels auto-scale to the largest house magnitude (thousands/millions) that fits the data,
  // so long values stay horizontal; tooltips keep the output's exact configured format for precision.
  const maxAbs = Math.max(0, ...map(sortedRows, (d) => Math.abs(d.Value)));
  const autoTruncation =
    config.formatType === 'Percent'
      ? 0
      : maxAbs >= 1e6
        ? 2
        : maxAbs >= 1e3
          ? 1
          : 0;
  const labelConfig = {
    ...config,
    magnitudeTruncation: Math.max(config.magnitudeTruncation, autoTruncation),
  };
  // The header above the chart carries the semantic ("Effect on <output>"); the value-axis title below
  // carries only the unit of measurement (the auto-picked house magnitude).
  const unitLabel =
    labelConfig.magnitudeTruncation === 2
      ? 'Millions'
      : labelConfig.magnitudeTruncation === 1
        ? 'Thousands'
        : '';

  // Content-driven height: a fixed band per bar keeps bars readable at any count and removes the dead
  // space below the chart when there are few bars; the page scrolls when there are many.
  const BAR_BAND_HEIGHT = 44;
  const CHROME_HEIGHT = 170; // title above + value axis and its unit label below
  const chartHeight = Math.max(
    250,
    sortedRows.length * BAR_BAND_HEIGHT + CHROME_HEIGHT
  );

  const options: Highcharts.Options = {
    chart: {
      type: 'bar',
      height: chartHeight,
    },

    title: {
      text: outputName ? `Effect on ${outputName}` : '',
      align: 'left',
      margin: 28,
      style: {
        fontSize: `${sizes.title}px`,
        fontWeight: '500',
      },
    },

    xAxis: {
      type: 'category',
      labels: {
        // Chip-styled labels double as the click affordance for the per-bar inputs popover
        useHTML: true,
        formatter: function () {
          return `<span class="tornado-bar-label" title="${escape(String(this.value))}">${escape(String(this.value))}</span>`;
        },
        style: {
          fontSize: `${sizes.barLabel}px`,
          fontWeight: 'normal',
        },
      },
    },
    yAxis: {
      title: {
        text: unitLabel,
        style: {
          fontSize: `${sizes.axisTitle}px`,
        },
      },
      // Padding keeps bar-end data labels inside the plot area on both the negative and positive sides
      minPadding: 0.08,
      maxPadding: 0.08,
      labels: {
        rotation: 0,
        formatter: function () {
          return formatValue(this.value as number, labelConfig);
        },
        style: {
          fontSize: `${sizes.axisValue}px`,
        },
      },
      plotLines: [
        {
          value: 0,
          width: 2,
          color: '#666666',
          zIndex: 3,
          // Baseline (A3): label the zero reference line with the baseline
          // value (Scenario A summary) the effect bars deviate from. Same
          // treatment in Reports and Exec (Brendan: identical in both).
          // Omitted when there's no summary row to read a baseline from.
          ...(baselineValue != null
            ? {
                label: {
                  text: `Baseline: ${formatValue(
                    Number(baselineValue),
                    config
                  )}`,
                  // Plot-line label alignment is AlignValue (left/center/right);
                  // 'right' sits it at the top end of the vertical zero line.
                  align: 'right' as const,
                  y: -6,
                  x: 4,
                  rotation: 0,
                  style: {
                    fontSize: `${sizes.axisValue}px`,
                    color: '#666666',
                    fontWeight: 'bold',
                  },
                },
              }
            : {}),
        },
      ],
    },
    legend: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    tooltip: {
      useHTML: true,
      formatter: function (this: Highcharts.TooltipFormatterContextObject) {
        const point = this.point as any;
        const config = getFormatConfig(formatKey);
        const value = formatValue(Number(this.y), config);

        return generateTooltipHTML(
          outputName,
          value,
          Number(this.y),
          config,
          point.groups,
          false
        );
      },
    },
    series: [
      {
        type: 'bar',
        pointPadding: 0,
        dataLabels: {
          enabled: true,
          crop: false,
          overflow: 'allow',
          formatter: function () {
            return formatValue(Number(this.y), labelConfig);
          },
          style: {
            fontSize: `${sizes.dataLabel}px`,
            fontWeight: 'normal',
            textOutline: 'none',
          },
        },
        data: map(sortedRows, (d) => {
          return {
            name: d.ColumnName,
            y: d.Value,
            groups: d.Groups,
            color:
              (d.Value as number) >= 0
                ? GRADIENT_CONFIG.POSITIVE_COLOR
                : GRADIENT_CONFIG.NEGATIVE_COLOR,
          };
        }),
      },
    ],
  };
  return (
    <>
      <Box
        onClick={handleLabelClick}
        sx={{
          '& .tornado-bar-label': {
            display: 'inline-block',
            maxWidth: 220,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            padding: '3px 10px',
            borderRadius: '14px',
            border: `1px solid ${grey[300]}`,
            backgroundColor: grey[100],
            fontWeight: 500,
            ...(columns?.length && { cursor: 'pointer' }),
          },
          ...(columns?.length && {
            '& .tornado-bar-label:hover': {
              backgroundColor: grey[200],
            },
          }),
        }}
      >
        <HighchartsReact highcharts={Highcharts} options={options} />
      </Box>
      <Popover
        open={!!barPopover}
        anchorReference="anchorPosition"
        anchorPosition={
          barPopover
            ? { left: barPopover.left, top: barPopover.top }
            : undefined
        }
        onClose={() => setBarPopover(null)}
        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, width: 360 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {barPopover?.barName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {barInputNames.length} forecast input
            {barInputNames.length === 1 ? '' : 's'} in this bar
          </Typography>
          {barInputNames.length > 10 && (
            <TextField
              fullWidth
              size="small"
              placeholder="Filter inputs"
              value={inputFilter}
              onChange={(e) => setInputFilter(e.target.value)}
              sx={{ mt: 1 }}
            />
          )}
          <List dense sx={{ maxHeight: 320, overflow: 'auto', mt: 0.5 }}>
            {map(filteredInputNames, (name) => (
              <ListItem key={name} disableGutters sx={{ py: 0.25 }}>
                <ListItemText
                  primary={name}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
          {filteredInputNames.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {barInputNames.length === 0
                ? 'No input details available for this bar.'
                : 'No inputs match the filter.'}
            </Typography>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default TornadoChartCore;
